import { useAccount } from "@apis/accounts/useAccounts"
import { useGetMarketCurrentPtcQuery } from "@apis/pricing/usePricing"
import { useGetUtilityQuery } from "@apis/utilities/useUtilities"
import type { TTitleValueItem } from "@components/TitleValueList/types"
import { formatDate, formatRate } from "@utils/formatters"
import { Link } from "react-router-dom"

type TAccountUtilityData = {
  items: TTitleValueItem[]
}

export const useAccountUtilitySerializer = (account_id: string) => {
  const getAccount = useAccount(account_id)
  const getUtility = useGetUtilityQuery(getAccount.data?.market_code)
  const getCurrentPtc = useGetMarketCurrentPtcQuery(
    getAccount.data?.market_code,
    getAccount.data?.revenue_class_code,
  )
  const isError =
    getAccount.isError || getUtility.isError || getCurrentPtc.isError

  const data: TAccountUtilityData = {
    items: [
      {
        title: { content: "Utility:" },
        value: {
          content: (() => {
            const marketCode = getAccount.data?.market_code
            const contractTypeCode = getAccount.data?.contract_type_code

            return (
              marketCode &&
              contractTypeCode && (
                <Link
                  to={`${import.meta.env.VITE_CUST_SITE}/utilities/${marketCode}/bill-samples/${contractTypeCode}`}
                  target="_blank"
                >
                  <span>{getAccount.data?.market_code}</span>
                </Link>
              )
            )
          })(),
          isPending: getAccount.isPending,
        },
      },
      {
        title: {
          content: "Utility Full Name:",
        },
        value: {
          content: getAccount.data?.utility_full_name ?? "N/A",
          isPending: getAccount.isPending,
          className: "tw:break-all",
        },
      },
      {
        title: {
          content: getAccount.data?.market?.account_num_synonym
            ? `${getAccount.data.market.account_num_synonym}:`
            : "Account Number:",
          isPending: getAccount.isPending,
        },
        value: {
          content: getAccount.data?.account_number,
          isPending: getAccount.isPending,
          className: "tw:break-all",
        },
      },
      {
        title: { content: "Current PTC:" },
        value: {
          content: (() => {
            const foundRate = getCurrentPtc.data?.found_rate
            const rate = formatRate(
              getCurrentPtc.data?.rate,
              getCurrentPtc.data?.rate_units,
            )
            const fromDt = formatDate(getCurrentPtc.data?.from_dt)
            const toDt = formatDate(getCurrentPtc.data?.to_dt)

            return (
              foundRate && (
                <div className="tw:flex tw:flex-col">
                  <span>{rate}</span>
                  {fromDt && <span>{`Start Date: ${fromDt}`}</span>}
                  {toDt && <span>{`End Date: ${toDt}`}</span>}
                </div>
              )
            )
          })(),
          isPending: getCurrentPtc.isPending,
        },
      },
      {
        title: { content: "Upcoming PTC:" },
        value: {
          content: (() => {
            const upcomingPtc = getCurrentPtc.data?.upcoming_price_to_compare
            const rate = formatRate(upcomingPtc?.rate, upcomingPtc?.rate_units)
            const fromDt = formatDate(upcomingPtc?.from_dt)

            return (
              rate &&
              fromDt && (
                <div className="tw:flex tw:flex-col">
                  <span>{`${rate} as of ${fromDt}`}</span>
                </div>
              )
            )
          })(),
          isPending: getCurrentPtc.isPending,
        },
      },
      {
        title: { content: "Usage:" },
        value: {
          content: (() => {
            const usageYtd = getAccount.data?.usage_ytd
            const avgMonthlyUsage = getAccount.data?.avg_monthly_usage
            const nativeUnitOfMeasure = getUtility.data?.native_unit_of_measure
            const avgAnnualUsageForecast =
              getAccount.data?.avg_annual_usage_forecast

            return (
              nativeUnitOfMeasure && (
                <div className="tw:flex tw:flex-col">
                  {!!usageYtd && (
                    <span>{`Year to date: ${usageYtd}/${nativeUnitOfMeasure}`}</span>
                  )}
                  {!!avgMonthlyUsage && (
                    <span>{`${avgMonthlyUsage} average ${nativeUnitOfMeasure}/month`}</span>
                  )}
                  {avgAnnualUsageForecast && (
                    <span>{`${avgAnnualUsageForecast} average ${nativeUnitOfMeasure}/year`}</span>
                  )}
                </div>
              )
            )
          })(),
          isPending: getUtility.isPending || getAccount.isPending,
        },
      },
      {
        title: { content: "Meter Read:" },
        value: {
          content: (() => {
            const previousCycleDt = formatDate(
              getAccount.data?.previous_cycle_dt,
            )
            const nextCycleDt = formatDate(getAccount.data?.next_cycle_dt)

            return (
              <div className="tw:flex tw:flex-col">
                {previousCycleDt && (
                  <span>{`Previous: ${previousCycleDt}`}</span>
                )}
                {nextCycleDt && <span>{`Next: ${nextCycleDt}`}</span>}
              </div>
            )
          })(),
          isPending: getAccount.isPending,
        },
      },
    ],
  }

  return {
    data,
    isError,
  }
}
